import { getUrl } from '../config';
import React from "react";
import styled from "@emotion/styled";
import { keyframes } from '@emotion/react';
import { io } from 'socket.io-client';
import PersonIcon from '@mui/icons-material/Person';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import {
  Grid,
  List,
  ListItemText as MuiListItemText,
  ListItemButton as MuiListItemButton,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";

const blinkAnimation = keyframes`
  0% { color: #ff0000; }
  50% { color: #ffffff; }
  100% { color: #ff0000; }
`;

const Wrapper = styled.div`
  padding: ${(props) => props.theme.spacing(0.25)}
    ${(props) => props.theme.spacing(4)};
  background: ${(props) => props.theme.footer.background};
  position: relative;
`;

const ListItemButton = styled(MuiListItemButton)`
  display: inline-block;
  width: auto;
  padding-left: ${(props) => props.theme.spacing(2)};
  padding-right: ${(props) => props.theme.spacing(2)};
`;

const ListItemText = styled(MuiListItemText)`
  span {
    color: ${(props) => props.theme.footer.color};
  }
`;

const BlinkingText = styled.p`
  margin: 0;
  font-weight: bold;
  animation: ${blinkAnimation} 2s infinite;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
`;

const PersonIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.footer.color};
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const ConnectedUsersTooltip = styled(Box)`
  padding: 8px;
  max-width: 300px;
`;

const UserStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InactiveIcon = styled(BedtimeIcon)`
  &.gray {
    color: #ffffff;
  }
  &.warning {
    color: #ff9800;
  }
  &.danger {
    color: #f44336;
  }
`;

const ActiveIcon = styled(FiberManualRecordIcon)`
  color: #4caf50;
  font-size: 12px;
`;

function Footer() {
  const [socket, setSocket] = React.useState(null);
  const [broadcastMessage, setBroadcastMessage] = React.useState('');
  const [broadcastSender, setBroadcastSender] = React.useState('');
  const [showBroadcast, setShowBroadcast] = React.useState(false);
  const [broadcastTimeout, setBroadcastTimeout] = React.useState(null);
  const [connectedUsers, setConnectedUsers] = React.useState([]);

  const handleCloseBroadcast = () => {
    setShowBroadcast(false);
    if (broadcastTimeout) {
      clearTimeout(broadcastTimeout);
      setBroadcastTimeout(null);
    }
  };

  // Gérer l'activité utilisateur
  React.useEffect(() => {
    const handleUserActivity = () => {
      if (socket) {
        socket.emit('userActivity');
      }
    };

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
    };
  }, [socket]);

  React.useEffect(() => {
    const newSocket = io(getUrl());
    setSocket(newSocket);

    const handleBroadcast = (data) => {
      setBroadcastMessage(data.message);
      setBroadcastSender(data.sender);
      setShowBroadcast(true);

      if (broadcastTimeout) {
        clearTimeout(broadcastTimeout);
      }

      const timeout = setTimeout(() => {
        setShowBroadcast(false);
      }, data.duration * 1000);

      setBroadcastTimeout(timeout);
    };

    newSocket.on('broadcast', handleBroadcast);
    newSocket.on('connectedUsersUpdate', (users) => {
      setConnectedUsers(users);
    });

    let userData = {
      id: localStorage.getItem('userId'),
      erpId: parseInt(localStorage.getItem('erpId'))
    };
    newSocket.emit('userConnected', userData);

    return () => {
      if (broadcastTimeout) {
        clearTimeout(broadcastTimeout);
      }
      newSocket.close();
    };
  }, []);

  const renderConnectedUsers = () => {
    const currentErpId = parseInt(localStorage.getItem('erpId'));
    const filteredUsers = connectedUsers.filter(user => user.erpId === currentErpId);

    return (
      <ConnectedUsersTooltip>
        <Typography variant="subtitle2" gutterBottom>
          Utilisateurs connectés ({filteredUsers.length})
        </Typography>
        {filteredUsers.map((user) => (
          <Typography
            key={user.id}
            variant="body2"
            sx={{
              fontWeight: String(user.id) === localStorage.getItem('userId') ? 'bold' : 'normal',
              color: String(user.id) === localStorage.getItem('userId') ? 'primary.main' : 'inherit'
            }}
          >
            <UserStatus>
              • {user.username || 'Utilisateur inconnu'}
              {user.isInactive ? (
                <Tooltip title={
                  user.inactivityLevel === 'danger' 
                    ? "Inactif depuis plus d'une heure"
                    : user.inactivityLevel === 'warning'
                    ? "Inactif depuis plus de 30 minutes"
                    : "Inactif depuis plus de 5 minutes"
                }>
                  <InactiveIcon 
                    fontSize="small" 
                    className={user.inactivityLevel}
                    sx={{ opacity: 0.7 }} 
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Actif">
                  <ActiveIcon />
                </Tooltip>
              )}
            </UserStatus>
          </Typography>
        ))}
      </ConnectedUsersTooltip>
    );
  };

  return (
    <Wrapper>
      <Grid container spacing={0} alignItems="center">
        <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <List sx={{ display: 'flex', padding: 0 }}>
                {showBroadcast && (
                  <ListItemButton onClick={handleCloseBroadcast}>
                    <Tooltip title={`${broadcastSender}: ${broadcastMessage}`} arrow>
                      <BlinkingText>
                        <strong>{broadcastSender}</strong>: {broadcastMessage}
                      </BlinkingText>
                    </Tooltip>
                  </ListItemButton>
                )}
              </List>
            </Grid>
            <Grid item>
              <Tooltip
                title={renderConnectedUsers()}
                arrow
                placement="top"
              >
                <PersonIconWrapper>
                  <PersonIcon fontSize="small" />
                </PersonIconWrapper>
              </Tooltip>
            </Grid>
            <Grid item>
              <List sx={{ display: 'flex', padding: 0 }}>
                <ListItemButton>
                  <ListItemText primary={`© ${new Date().getFullYear()} - Ylinar`} />
                </ListItemButton>
              </List>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Wrapper>
  );
}

export default Footer;
